import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { jwtDecode } from 'jwt-decode'
import { Pencil } from 'lucide-react'
import {
  Dialog,
  DialogContent,
} from '@edvi-tech/ui/dialog'
import Controls from '../../Components/Controls/Controls'
import Spinner from '../../Components/Progress/Spinner'
import { getFileInfo } from '../../Utils/FileOperations'
import FileUpload from '../../Components/FileUpload'
import DownloadCard from './DownloadCard'
import axiosGet from '../../Global/Axios/axiosGet'
import { getGeneralDate } from '../../Utils/DateTimeFormat'
import axiosPost from '../../Global/Axios/axiosPost'
import showSuccessSnackbar from '../../Components/Snackbar/successSnackbar'
import Logo from '../../Assets/Images/edvi-logo-blue.png'
import handleError from '../../Global/HandleError/handleError'
import axiosPut from '../../Global/Axios/axiosPut'
import PdfAnnotate from '../../PdfAnnotator/PdfAnnotate'
import { ExamStatusPill } from '../../Components/Cards/ExamCard'

const SubmitTestResultPageWithToken = ({ isLoggedIn }) => {
  const { batchId, testId } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [questionPapers, setQuestionPapers] = useState([])
  const [questionPaperError, setQuestionPaperError] = useState('')
  const [uploadingQuesPaper, setUploadingQuesPaper] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [submitBtnLoading, setSubmitBtnLoading] = useState(false)
  const [annotatePdfUrl, setAnnotatePdfUrl] = useState(undefined)
  const [testDetails, setTestDetails] = useState(null)
  const [attachedQuestionPapers, setAttachedQuestionPapers] = useState([])
  const [userToken, setUserToken] = useState(undefined)
  const [userId, setUserId] = useState(undefined)

  const MAX_FILE_SIZE = 50

  const uploadPaperWithToken = async (token, file, setProgress) => {
    try {
      if (!file) return undefined
      const formData = new FormData()
      formData.append('file', file)
      const fileInfo = getFileInfo(file)

      const storageUrlRes = await axiosPost(
        `/batch-test/${testId}/get-storage-url/`,
        {
          headers: { 'X-Resource-Token': token },
          data: {
            file_format: fileInfo.type,
            file_type: 'submission',
          },
        },
      )

      const storageUrl = storageUrlRes.data.submission
      const storagePath = storageUrl.split('?')[0]

      const res = await axiosPut(storageUrl, {
        data: formData,
        onUploadProgress: (progressEvent) => {
          const { loaded, total } = progressEvent
          setProgress(Math.round((loaded * 100) / total))
        },
      })

      if (res.status === 200) {
        showSuccessSnackbar( 'File Uploaded Successfully')
        return storagePath
      }
    } catch (err) {
      handleError(err)
      navigate('/dashboard')
    }
    return undefined
  }

  const uploadQuestionPapers = async (file) => {
    setQuestionPaperError('')
    const fileInfo = getFileInfo(file)
    if (fileInfo.sizeInMB > MAX_FILE_SIZE) {
      setQuestionPaperError(`File size cannot exceed ${MAX_FILE_SIZE}MB`)
      return
    }

    setUploadingQuesPaper(true)
    const response = await uploadPaperWithToken(
      userToken,
      file,
      setUploadProgress,
    )
    setQuestionPapers((prev) => {
      const updated = [...prev, { name: file.name, url: response }]
      return Array.from(
        new Map(
          updated
            .filter((f) => f && f.url)
            .map((f) => [f.url, f]),
        ).values(),
      )
    })
    setUploadingQuesPaper(false)
  }

  const handleSubmission = async () => {
    setSubmitBtnLoading(true)
    const res = await axiosPost(`/batch-test/${testId}/submit-with-token/`, {
      headers: { 'X-Resource-Token': userToken },
      data: {
        submission_papers: questionPapers.map((f) => f.url),
      },
    })
    setSubmitBtnLoading(false)
    if (res.status === 200) navigate('/dashboard')
  }

  const getTestDetailsWithToken = async (token) => {
    try {
      const response = await axiosGet(`batch-test/${testId}/token-action`, {
        params: { token },
      })
      setTestDetails(response.data)
      setAttachedQuestionPapers(response.data?.question_papers ?? [])
    } catch (err) {
      handleError(err)
      navigate('/dashboard')
    }
  }

  const studentSubs = useMemo(
    () =>
      (testDetails?.submissions ?? []).filter(
        (submission) => submission?.submitted_by === userId,
      ),
    [testDetails, userId],
  )

  const isAlreadySubmitted = studentSubs.length > 0

  const statusMeta = useMemo(() => {
    const submissionCount = studentSubs.length
    const evaluationCount = studentSubs.filter(
      (s) => (s?.corrected_papers?.length ?? 0) > 0,
    ).length
    if (submissionCount === 0) {
      return { label: 'Submission Pending', tone: 'warn' }
    }
    if (evaluationCount < submissionCount) {
      return { label: 'Evaluation Pending', tone: 'pending' }
    }
    return { label: 'Evaluated', tone: 'ok' }
  }, [studentSubs])

  useEffect(() => {
    const token = searchParams.get('token')
    if (!token || isLoggedIn) {
      if (!token) navigate('/dashboard')
      else navigate(`/dashboard/examDetails/${batchId}/${testId}`)
      return
    }
    setUserToken(token)
    const decoded = jwtDecode(token)
    setUserId(decoded.sub)
    getTestDetailsWithToken(token)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams])

  const chapterNames = (testDetails?.chapters_relation ?? [])
    .map((item) => item.chapter_name)
    .filter(Boolean)
    .join(', ')

  return (
    <div className="edvi-exam-token">
      <div className="edvi-exam-token__shell">
        <header className="edvi-exam-token__top">
          <Link to="/">
            <img src={Logo} alt="edvi" className="edvi-exam-token__brand" />
          </Link>
          <p className="edvi-exam-token__crumb">
            {testDetails?.batch_name ?? batchId} · Test submission
          </p>
        </header>

        <div className="edvi-exam-token__body">
          <section className="edvi-exam-panel">
            <dl className="edvi-exam-dl">
              <div className="edvi-exam-dl__item">
                <dt className="edvi-exam-dl__label">Conducted on</dt>
                <dd className="edvi-exam-dl__value">
                  {getGeneralDate(testDetails?.conducted_on) || '—'}
                </dd>
              </div>
              <div className="edvi-exam-dl__item">
                <dt className="edvi-exam-dl__label">Submit by</dt>
                <dd className="edvi-exam-dl__value">
                  {getGeneralDate(testDetails?.last_date_for_submission) ||
                    '—'}
                </dd>
              </div>
              {testDetails?.max_marks != null && (
                <div className="edvi-exam-dl__item">
                  <dt className="edvi-exam-dl__label">Max marks</dt>
                  <dd className="edvi-exam-dl__value">
                    {Number(testDetails.max_marks)}
                  </dd>
                </div>
              )}
              <div className="edvi-exam-dl__item">
                <dt className="edvi-exam-dl__label">Status</dt>
                <dd className="edvi-exam-dl__value">
                  <ExamStatusPill {...statusMeta} />
                </dd>
              </div>
              {testDetails?.topics_covered && (
                <div className="edvi-exam-dl__item edvi-exam-dl__item--full">
                  <dt className="edvi-exam-dl__label">Topic</dt>
                  <dd className="edvi-exam-dl__value">
                    {testDetails.topics_covered}
                  </dd>
                </div>
              )}
              {chapterNames && (
                <div className="edvi-exam-dl__item edvi-exam-dl__item--full">
                  <dt className="edvi-exam-dl__label">Chapters covered</dt>
                  <dd className="edvi-exam-dl__value">{chapterNames}</dd>
                </div>
              )}
            </dl>
          </section>

          <section className="edvi-exam-panel">
            <h2 className="edvi-exam-panel__title">Question papers</h2>
            <div className="edvi-exam-files">
              {attachedQuestionPapers.map((item) => (
                <div className="edvi-exam-file-row" key={item}>
                  <DownloadCard fileUrl={item} />
                  {!isAlreadySubmitted && (
                    <button
                      type="button"
                      className="edvi-exam-file__btn"
                      aria-label="Annotate PDF"
                      onClick={() => setAnnotatePdfUrl(item)}
                    >
                      <Pencil size={14} strokeWidth={2.25} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>

          {!isAlreadySubmitted && (
            <section className="edvi-exam-panel">
              <h2 className="edvi-exam-panel__title">Submission papers</h2>
              <div className="edvi-exam-uploads">
                <div className="edvi-exam-upload">
                  <FileUpload
                    id="tokenSubmissionPapers"
                    uploadFile={uploadQuestionPapers}
                    uploadProgress={uploadProgress}
                    showProgress={uploadingQuesPaper}
                    disabled={questionPapers.length > 8}
                    acceptedFileTypes="image/jpeg,image/png,image/jpg,application/pdf"
                  />
                </div>
                {questionPapers.map((file) => (
                  <div className="edvi-exam-upload" key={file.url || file.name}>
                    <FileUpload file={file} />
                  </div>
                ))}
              </div>
              {questionPaperError && (
                <p className="edvi-exam-form__error">{questionPaperError}</p>
              )}
            </section>
          )}

          {isAlreadySubmitted && (
            <section className="edvi-exam-panel">
              <h2 className="edvi-exam-panel__title">Submissions</h2>
              <div className="edvi-exam-files">
                {studentSubs
                  .flatMap((item) => item.submission_papers ?? [])
                  .map((item) => (
                    <DownloadCard key={item} fileUrl={item} />
                  ))}
              </div>
            </section>
          )}

          {isAlreadySubmitted &&
            (studentSubs[0]?.corrected_papers ?? []).length > 0 && (
              <section className="edvi-exam-panel">
                <h2 className="edvi-exam-panel__title">Corrected papers</h2>
                <div className="edvi-exam-files">
                  {studentSubs
                    .flatMap((item) => item.corrected_papers ?? [])
                    .map((item) => (
                      <DownloadCard key={item} fileUrl={item} />
                    ))}
                </div>
              </section>
            )}
        </div>

        <div className="edvi-exam-token__footer">
          <Controls.Button
            fullWidth
            text={isAlreadySubmitted ? 'Already Submitted' : 'Submit'}
            disabled={questionPapers.length === 0 || isAlreadySubmitted}
            startIcon={
              submitBtnLoading ? (
                <Spinner size={18} color="inherit" />
              ) : undefined
            }
            onClick={handleSubmission}
          />
        </div>
      </div>

      <Dialog
        open={Boolean(annotatePdfUrl)}
        onOpenChange={(next) => {
          if (!next) setAnnotatePdfUrl(undefined)
        }}
      >
        <DialogContent className="edvi-dlg-content--wide edvi-pdf-annotate-dlg">
          {annotatePdfUrl && (
            <PdfAnnotate
              documentURL={annotatePdfUrl}
              onSuccess={(file) => {
                uploadQuestionPapers(file)
                setAnnotatePdfUrl(undefined)
              }}
              onCancel={() => setAnnotatePdfUrl(undefined)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default SubmitTestResultPageWithToken
